import './styles.scss'
import UnbeatableSection from '../../Components/UnbeatableSection'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'
import AnimateOnScroll from '../../Components/AnimateOnScroll'
import { SERVICES } from '../../constants'
import { useState } from 'react'

const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID
const templateId = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID
const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const REQUIRED = ['name', 'email', 'service', 'subject', 'message']

const validate = (name, value) => {
  switch (name) {
    case 'name':
      return value.trim() ? '' : 'Full name is required.'
    case 'email':
      if (!value.trim()) return 'Email address is required.'
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? ''
        : 'Please enter a valid email address.'
    case 'service':
      return value ? '' : 'Please select a service.'
    case 'subject':
      return value.trim() ? '' : 'Subject is required.'
    case 'message':
      return value.trim() ? '' : 'Message is required.'
    default:
      return ''
  }
}

const ContactUs = () => {
  const [honeypot, setHoneypot] = useState('')

  const { form, onChange, reset } = useForm({
    name: '',
    email: '',
    phone: '',
    service: '',
    subject: '',
    message: '',
    loading: false,
    success: false,
    error: null,
  })

  const [touched, setTouched] = useState({})
  const [fieldErrors, setFieldErrors] = useState({})

  const handleChange = (e) => {
    onChange(e.target.name, e.target.value)
    if (touched[e.target.name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [e.target.name]: validate(e.target.name, e.target.value),
      }))
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    setTouched((prev) => ({ ...prev, [name]: true }))
    setFieldErrors((prev) => ({ ...prev, [name]: validate(name, value) }))
  }

  const fieldState = (name) => {
    if (!touched[name]) return ''
    return fieldErrors[name] ? 'error' : 'valid'
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const allTouched = REQUIRED.reduce((acc, k) => ({ ...acc, [k]: true }), {})
    const errors = REQUIRED.reduce(
      (acc, k) => ({ ...acc, [k]: validate(k, form[k]) }),
      {}
    )

    setTouched((prev) => ({ ...prev, ...allTouched }))
    setFieldErrors((prev) => ({ ...prev, ...errors }))

    if (REQUIRED.some((k) => errors[k])) return

    // honeypot check — bots fill this, humans don't
    if (honeypot) return

    onChange('loading', true)
    onChange('error', null)
    onChange('success', false)

    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          service: form.service,
          subject: form.subject,
          msg: form.message,
        },
        publicKey
      )
      reset()
      setTouched({})
      setFieldErrors({})
      onChange('success', true)
    } catch {
      onChange('error', 'Something went wrong. Please try again.')
    }

    onChange('loading', false)
  }

  return (
    <div className="contact-us">
      <div className="contact-us__top">
        <h1>Contact Us</h1>
      </div>

      <div className="contact-us__content">
        <section className="contact-us__main">
          <AnimateOnScroll animation="fade-right" className="contact-us__info">
            <h2>Get In Touch</h2>
            <p>
              Farze Global Logistics is available around the clock to meet your
              shipping and logistics needs. Reach out for a free quote and join
              our growing family of satisfied customers.
            </p>

            <div className="contact-us__info__items">
              <div className="contact-us__info__item">
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('phone')} />
                </div>
                <div>
                  <span className="contact-us__info__item__label">Phone</span>
                  <span className="contact-us__info__item__value">+44-7687287658</span>
                </div>
              </div>

              <div className="contact-us__info__item">
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('envelope')} />
                </div>
                <div>
                  <span className="contact-us__info__item__label">Email</span>
                  <span className="contact-us__info__item__value">contact@farzelogistics.com</span>
                </div>
              </div>

              <div className="contact-us__info__item">
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('location-dot')} />
                </div>
                <div>
                  <span className="contact-us__info__item__label">Address</span>
                  <span className="contact-us__info__item__value">Global Facility Office</span>
                </div>
              </div>

              <div className="contact-us__info__item">
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('clock')} />
                </div>
                <div>
                  <span className="contact-us__info__item__label">Working Hours</span>
                  <span className="contact-us__info__item__value">Mon – Fri: 8:00am – 6:00pm</span>
                </div>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-left" delay={150} className="contact-us__form-card">
            <div className="contact-us__form-header">
              <h3>Send Us a Message</h3>
              <span className="contact-us__required-note">
                <span className="req">*</span> Required fields
              </span>
            </div>

            {form.success && (
              <div className="contact-us__alert contact-us__alert--success">
                <FontAwesomeIcon icon={solid('circle-check')} />
                Message sent! We'll get back to you shortly.
              </div>
            )}

            {form.error && (
              <div className="contact-us__alert contact-us__alert--error">
                <FontAwesomeIcon icon={solid('circle-exclamation')} />
                {form.error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot — hidden from real users, bots fill it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="contact-us__form-row">
                <div className={`contact-us__field contact-us__field--${fieldState('name')}`}>
                  <label htmlFor="name">
                    Full Name <span className="req">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={form.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.name && fieldErrors.name && (
                    <span className="contact-us__field__error">{fieldErrors.name}</span>
                  )}
                </div>

                <div className={`contact-us__field contact-us__field--${fieldState('email')}`}>
                  <label htmlFor="email">
                    Email Address <span className="req">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.email && fieldErrors.email && (
                    <span className="contact-us__field__error">{fieldErrors.email}</span>
                  )}
                </div>
              </div>

              <div className="contact-us__form-row">
                <div className="contact-us__field">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+44 000 000 0000"
                    value={form.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>

                <div className={`contact-us__field contact-us__field--${fieldState('service')}`}>
                  <label htmlFor="service">
                    Service of Interest <span className="req">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="" disabled>Select a service...</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {touched.service && fieldErrors.service && (
                    <span className="contact-us__field__error">{fieldErrors.service}</span>
                  )}
                </div>
              </div>

              <div className={`contact-us__field contact-us__field--${fieldState('subject')}`}>
                <label htmlFor="subject">
                  Subject <span className="req">*</span>
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="How can we help?"
                  value={form.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.subject && fieldErrors.subject && (
                  <span className="contact-us__field__error">{fieldErrors.subject}</span>
                )}
              </div>

              <div className={`contact-us__field contact-us__field--${fieldState('message')}`}>
                <label htmlFor="message">
                  Message <span className="req">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  placeholder="Tell us more about your shipment or enquiry..."
                  value={form.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.message && fieldErrors.message && (
                  <span className="contact-us__field__error">{fieldErrors.message}</span>
                )}
              </div>

              <button
                type="submit"
                className="contact-us__submit"
                disabled={form.loading}
              >
                {form.loading ? (
                  <>
                    <FontAwesomeIcon icon={solid('circle-notch')} spin />
                    Sending...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={solid('paper-plane')} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </AnimateOnScroll>
        </section>

        <UnbeatableSection />
      </div>
    </div>
  )
}

export default ContactUs
